import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './AssistenteWidget.module.css';

type Bloco =
  | { tipo: 'p'; texto: string }
  | { tipo: 'h'; texto: string }
  | { tipo: 'ul'; itens: string[] }
  | { tipo: 'ol'; itens: string[] }
  | { tipo: 'tabela'; cabecalho: string[]; linhas: string[][] };

function celulas(linha: string): string[] {
  return linha.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

function separadorTabela(linha: string): boolean {
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(linha);
}

function caminhoInterno(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('://');
}

function inline(texto: string, chave: string): ReactNode[] {
  const pedacos: ReactNode[] = [];
  const marca = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let cursor = 0;
  let indice = 0;
  for (const encontrado of texto.matchAll(marca)) {
    const inicio = encontrado.index ?? 0;
    if (inicio > cursor) {
      pedacos.push(texto.slice(cursor, inicio));
    }
    if (encontrado[1]) {
      pedacos.push(<strong key={`${chave}-n${indice}`}>{encontrado[1]}</strong>);
    } else if (encontrado[2]) {
      pedacos.push(<em key={`${chave}-i${indice}`}>{encontrado[2]}</em>);
    } else if (encontrado[3] && encontrado[4] && caminhoInterno(encontrado[4])) {
      pedacos.push(
        <Link key={`${chave}-l${indice}`} className={styles.link} to={encontrado[4]}>
          {encontrado[3]}
        </Link>,
      );
    } else if (encontrado[3]) {
      pedacos.push(encontrado[3]);
    }
    cursor = inicio + encontrado[0].length;
    indice += 1;
  }
  if (cursor < texto.length) {
    pedacos.push(texto.slice(cursor));
  }
  return pedacos;
}

export function montarBlocos(texto: string): Bloco[] {
  const blocos: Bloco[] = [];
  let paragrafo: string[] = [];
  let lista: { tipo: 'ul' | 'ol'; itens: string[] } | null = null;

  function fecharParagrafo() {
    if (paragrafo.length === 0) return;
    blocos.push({ tipo: 'p', texto: paragrafo.join(' ') });
    paragrafo = [];
  }

  function fecharLista() {
    if (!lista) return;
    blocos.push({ tipo: lista.tipo, itens: lista.itens });
    lista = null;
  }

  const todas = texto.replace(/\r\n/g, '\n').split('\n');
  for (let i = 0; i < todas.length; i += 1) {
    const linha = todas[i].trim();
    // Tabela markdown: | a | b | seguida de |---|---|, e as linhas seguintes que comecam por |.
    if (linha.startsWith('|') && i + 1 < todas.length && separadorTabela(todas[i + 1].trim())) {
      fecharParagrafo();
      fecharLista();
      const cabecalho = celulas(linha);
      const linhas: string[][] = [];
      i += 2;
      while (i < todas.length && todas[i].trim().startsWith('|')) {
        linhas.push(celulas(todas[i].trim()));
        i += 1;
      }
      i -= 1;
      blocos.push({ tipo: 'tabela', cabecalho, linhas });
      continue;
    }
    if (!linha) {
      fecharParagrafo();
      fecharLista();
      continue;
    }
    const titulo = /^(#{1,3})\s+(.+)$/.exec(linha);
    if (titulo) {
      fecharParagrafo();
      fecharLista();
      blocos.push({ tipo: 'h', texto: titulo[2] });
      continue;
    }
    const item = /^(\d+)\.\s+(.+)$/.exec(linha) ?? /^[-*]\s+(.+)$/.exec(linha);
    if (item) {
      fecharParagrafo();
      const ordenada = /^\d+\./.test(linha);
      const tipo = ordenada ? 'ol' : 'ul';
      const conteudo = item[item.length - 1];
      if (!lista || lista.tipo !== tipo) {
        fecharLista();
        lista = { tipo, itens: [] };
      }
      lista.itens.push(conteudo);
      continue;
    }
    fecharLista();
    paragrafo.push(linha);
  }
  fecharParagrafo();
  fecharLista();
  return blocos;
}

export function TextoAssistente({ texto }: { texto: string }) {
  const blocos = montarBlocos(texto);
  return (
    <div className={styles.markdown}>
      {blocos.map((bloco, indice) => {
        if (bloco.tipo === 'h') {
          return <h3 key={indice}>{inline(bloco.texto, String(indice))}</h3>;
        }
        if (bloco.tipo === 'tabela') {
          return (
            <div key={indice} className={styles.tabelaRolagem}>
              <table className={styles.tabela}>
                <thead>
                  <tr>
                    {bloco.cabecalho.map((celula, c) => (
                      <th key={c}>{inline(celula, `${indice}-h${c}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bloco.linhas.map((linha, l) => (
                    <tr key={l}>
                      {linha.map((celula, c) => (
                        <td key={c}>{inline(celula, `${indice}-${l}-${c}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (bloco.tipo === 'ul' || bloco.tipo === 'ol') {
          const Tag = bloco.tipo === 'ul' ? 'ul' : 'ol';
          return (
            <Tag key={indice}>
              {bloco.itens.map((item, itemIndice) => (
                <li key={itemIndice}>{inline(item, `${indice}-${itemIndice}`)}</li>
              ))}
            </Tag>
          );
        }
        return <p key={indice}>{inline(bloco.texto, String(indice))}</p>;
      })}
    </div>
  );
}
